import {
    type ReactNode,
    useState,
    useMemo
} from "react";
import {
    useWindowDimensions,
    StyleSheet,
    View
} from "react-native";
import {
    type ElevationLevelType,
    type GapPropagationType,
    mergeTypographyTokens,
    type ControlSizeType,
    getControlMeasures,
    type SharpnessType,
    getElevationStyle,
    type SurfaceType,
    NCoreUIKitTheme,
    usePointerType,
    SURFACE_LEVELS,
    getCornerStyle,
    CONTROL_SIZES,
    useBreakpoint,
    ToggleGroup,
    mergeShapes,
    concentric,
    getHitArea,
    TextInput,
    webStyle,
    Button,
    Chip,
    Card,
    Icon,
    Text
} from "ncore-ui-kit";
import {
    getKitLocale,
    useDocsI18n
} from "../../i18n";
import type {
    DocsTranslate
} from "../../anatomy/types";
import type {
    DocsTocItem
} from "../../components/toc";
import {
    X as XIcon
} from "lucide-react-native";
import {
    type DocsStyleTokens,
    PageHeader,
    BulletList,
    InlineCode,
    DocsTable,
    CodeBlock,
    SubTitle,
    Article,
    Section,
    Preview,
    Callout,
    Prose,
    Stats,
    Term
} from "../../components/content";
import themeJSON from "../../../../src/variants/themes/default.json";

type SpaceName = keyof DocsStyleTokens["spaces"];

type RadiusName = keyof DocsStyleTokens["radiuses"];

type BorderName = keyof DocsStyleTokens["borders"];

type LayerName = keyof DocsStyleTokens["shapeConfigs"]["layers"];

type TouchName = keyof DocsStyleTokens["touchTargets"];

type WidthName = "tooltipMaxWidth" | "contentMaxWidth" | "readingMaxWidth" | "dialogMaxWidth" | "sheetMaxWidth" | "sideListWidth" | "fieldMinWidth" | "commandWidth" | "drawerWidth" | "cardWidth" | "chatWidth";

type ChartName = "chartTooltipMinWidth" | "chartRadialThickness" | "chartSegmentGap" | "chartDonutRatio" | "chartDotSize" | "chartHeight" | "chartBarMax";

type LayerRowType = {
    name: LayerName;
    textKey: string;
    users: string;
};

type WidthRowType = {
    name: WidthName;
    users: string;
};

type ChartRowType = {
    textKey: string;
    name: ChartName;
};

type BorderRowType = {
    name: BorderName;
    textKey: string;
};

type TouchRowType = {
    standardKey: string;
    whereKey: string;
    name: TouchName;
};

const DEFAULT_TOKENS: NCoreUIKit.Palette = themeJSON;

const TERM_PATTERN = /\{\{term:([a-zA-Z0-9]+)\}\}/;

const GRID_UNIT = 4;

const ICON_SCALE = 1.25;

const SHARPNESS_SETS: Array<SharpnessType> = [
    "soft",
    "sharp",
    "rounded"
];

const RADIUS_ROWS: Array<RadiusName> = [
    "none",
    "xs",
    "sm",
    "md",
    "lg",
    "xl",
    "full",
    "form",
    "actions",
    "chip",
    "surface",
    "chartBar"
];

const CONTROL_SIZE_NAMES: Array<ControlSizeType> = [
    "small",
    "medium",
    "large"
];

const CONCENTRIC_INSETS: Array<SpaceName> = [
    "spacingXs",
    "spacingSm",
    "spacingMd"
];

const SURFACE_NAMES: Record<SurfaceType, string> = {
    popover: "Popover",
    dialog: "Dialog",
    sheet: "Sheet",
    toast: "Toast",
    card: "Card"
};

const TOUCH_ROWS: Array<TouchRowType> = [
    {
        standardKey: "spacingPage.touchFineStandard",
        whereKey: "spacingPage.touchFineWhere",
        name: "fine"
    },
    {
        standardKey: "spacingPage.touchCoarseStandard",
        whereKey: "spacingPage.touchCoarseWhere",
        name: "coarse"
    },
    {
        standardKey: "spacingPage.touchPreferredStandard",
        whereKey: "spacingPage.touchPreferredWhere",
        name: "preferred"
    },
    {
        standardKey: "spacingPage.touchHandleStandard",
        whereKey: "spacingPage.touchHandleWhere",
        name: "handle"
    }
];

const BORDER_ROWS: Array<BorderRowType> = [
    {
        textKey: "spacingPage.borderLine",
        name: "line"
    },
    {
        textKey: "spacingPage.borderSeparator",
        name: "separator"
    },
    {
        textKey: "spacingPage.borderSelection",
        name: "selection"
    },
    {
        textKey: "spacingPage.borderFocus",
        name: "focus"
    },
    {
        textKey: "spacingPage.borderHalo",
        name: "halo"
    },
    {
        textKey: "spacingPage.borderSubtract",
        name: "subtract"
    },
    {
        textKey: "spacingPage.borderIndicator",
        name: "indicator"
    },
    {
        textKey: "spacingPage.borderChartLine",
        name: "chartLine"
    }
];

const LAYER_ROWS: Array<LayerRowType> = [
    {
        users: "AttachedGroup, MessageScroller",
        textKey: "spacingPage.layerBase",
        name: "base"
    },
    {
        users: "AttachedGroup, AvatarGroup, Slider, Tabs, Toast",
        textKey: "spacingPage.layerRaised",
        name: "raised"
    },
    {
        textKey: "spacingPage.layerSticky",
        users: "MainHeader, Table",
        name: "sticky"
    },
    {
        textKey: "spacingPage.layerOverlay",
        name: "overlay",
        users: "—"
    }
];

const WIDTH_ROWS: Array<WidthRowType> = [
    {
        name: "contentMaxWidth",
        users: "PageContainer"
    },
    {
        users: "MessageScroller, Timeline",
        name: "chatWidth"
    },
    {
        name: "commandWidth",
        users: "Command"
    },
    {
        name: "sheetMaxWidth",
        users: "BottomSheet"
    },
    {
        name: "readingMaxWidth",
        users: "MarkdownViewer"
    },
    {
        users: "Dialog, Modal, Sheet, DataTable, Timeline, TreeView",
        name: "dialogMaxWidth"
    },
    {
        users: "Attachment, Citation, Trace",
        name: "cardWidth"
    },
    {
        users: "Sidebar, Menu, EmbeddedMenu",
        name: "drawerWidth"
    },
    {
        users: "Tooltip, Slider, Chart",
        name: "tooltipMaxWidth"
    },
    {
        users: "Popover, HoverCard, NavigationMenu, Tabs, Attachment, Breadcrumb, DataTable, Sidebar, Timeline",
        name: "sideListWidth"
    },
    {
        users: "TextInput, SelectBox, Combobox",
        name: "fieldMinWidth"
    }
];

const CHART_ROWS: Array<ChartRowType> = [
    {
        textKey: "spacingPage.chartHeightText",
        name: "chartHeight"
    },
    {
        textKey: "spacingPage.chartTooltipText",
        name: "chartTooltipMinWidth"
    },
    {
        textKey: "spacingPage.chartBarText",
        name: "chartBarMax"
    },
    {
        textKey: "spacingPage.chartRadialText",
        name: "chartRadialThickness"
    },
    {
        textKey: "spacingPage.chartDotText",
        name: "chartDotSize"
    },
    {
        textKey: "spacingPage.chartGapText",
        name: "chartSegmentGap"
    },
    {
        textKey: "spacingPage.chartDonutText",
        name: "chartDonutRatio"
    }
];

const SPACING_CODE = `const {
    spaces
} = NCoreUIKitTheme.useContext();

const style = {
    paddingHorizontal: spaces.spacingMd,
    gap: spaces.spacingSm
};`;

const DENSITY_CODE = `setupNCoreUIKit({
    initialSelectedGapPropagation: "compact"
});

NCoreUIKitTheme.switch({
    gapPropagation: "spacious"
});

<Button
    customTheme={{
        gapPropagation: "compact"
    }}
    title="Save"
/>`;

const CONTROL_CODE = `const {
    controlPaddings,
    controlHeights,
    shapeConfigs,
    touchTargets,
    iconSizes
} = NCoreUIKitTheme.useContext();

const pointerType = usePointerType();

const measures = getControlMeasures({
    controlPaddings,
    controlHeights,
    shapeConfigs,
    size: "medium",
    iconSizes
});

const hitArea = getHitArea({
    visualSize: measures.minHeight,
    touchTargets,
    pointerType
});`;

const CONCENTRIC_CODE = `const {
    radiuses,
    spaces
} = NCoreUIKitTheme.useContext();

const rowRadius = concentric({
    inset: spaces.spacingXs,
    outer: radiuses.surface,
    role: radiuses.actions
});`;

const ELEVATION_CODE = `const {
    shadowAlpha,
    elevations,
    colors
} = NCoreUIKitTheme.useContext();

const style = getElevationStyle({
    shadowColor: colors.system.shadow,
    level: "level2",
    shadowAlpha,
    elevations
});`;

const stylesheet = StyleSheet.create({
    centeredRow: {
        justifyContent: "center",
        flexDirection: "row",
        alignItems: "center",
        flexWrap: "wrap"
    },
    figureStage: {
        justifyContent: "center",
        alignItems: "center"
    },
    stack: {
        flexDirection: "column",
        alignItems: "flex-start"
    },
    figure: {
        flexDirection: "column",
        alignItems: "center"
    },
    row: {
        flexDirection: "row",
        alignItems: "center",
        flexWrap: "wrap"
    },
    toggle: {
        alignSelf: "center",
        maxWidth: "100%"
    },
    label: {
        textAlign: "center"
    }
});

const getIsCornerShapeSupported = () => typeof CSS !== "undefined" && typeof CSS.supports === "function" && CSS.supports("corner-shape", "squircle");

const formatNumber = (value: number, locale: string) => new Intl.NumberFormat(locale, {
    maximumFractionDigits: 2
}).format(value);

const formatPercent = (value: number, locale: string) => new Intl.NumberFormat(locale, {
    maximumFractionDigits: 1,
    style: "percent"
}).format(value);

const formatList = (items: Array<string | number>) => items.join(", ");

const formatSteps = (items: Array<number>) => items.join(" / ");

const getGridText = (value: number, locale: string) => `${GRID_UNIT} × ${formatNumber(value / GRID_UNIT, locale)}`;

const getChangedKeys = <T extends Record<string, number>>(first: T, second: T) => (Object.keys(first) as Array<keyof T & string>).filter((key) => first[key] !== second[key]);

const getEqualKeys = <T extends Record<string, number>>(first: T, second: T) => (Object.keys(first) as Array<keyof T & string>).filter((key) => first[key] === second[key]);

const getSurfaceUsers = (level: ElevationLevelType) => (Object.keys(SURFACE_LEVELS) as Array<SurfaceType>).filter((surface) => SURFACE_LEVELS[surface].level === level).map((surface) => SURFACE_NAMES[surface]);

const getTokenSets = (projectThemes?: NCoreUIKit.PartialPalette) => ({
    typography: mergeTypographyTokens({
        projectTypographyTokens: projectThemes?.typography as NCoreUIKit.TypographyWithVariants,
        defaultTypographyTokens: DEFAULT_TOKENS.typography
    }),
    shapes: mergeShapes({
        projectShapes: projectThemes?.shapes as NCoreUIKit.Shapes,
        defaultShapes: DEFAULT_TOKENS.shapes
    })
});

const getDensitySet = (tokens: ReturnType<typeof getTokenSets>, density: GapPropagationType) => ({
    controlPaddings: tokens.shapes.controlPaddings[density],
    controlHeights: tokens.shapes.controlHeights[density],
    typography: tokens.typography[density],
    spaces: tokens.shapes.spaces[density]
});

const getTokenValues = (record: object) => Object.values(record).filter((value): value is number => typeof value === "number");

const getSizeValues = (record: Record<ControlSizeType, number>) => CONTROL_SIZE_NAMES.map((size) => record[size]);

const isSharpness = (value: string): value is SharpnessType => SHARPNESS_SETS.some((item) => item === value);

const isInsetName = (value: string): value is SpaceName => CONCENTRIC_INSETS.some((item) => item === value);

const handleDemoPress = () => undefined;

const renderTerms = (text: string): Array<ReactNode> => text.split(TERM_PATTERN).map((part, index) => index % 2 === 1 ? <Term
    key={`${part}-${index}`}
    id={part}
/> : part);

const useSpacingStyles = () => {
    const {
        shapeConfigs,
        avatarSizes,
        spaces
    } = NCoreUIKitTheme.useContext();

    return useMemo(() => ({
        elevationGrid: {
            gridTemplateColumns: `repeat(auto-fit, minmax(min(100%, ${shapeConfigs.contentMaxWidth / 8}px), 1fr))`,
            paddingBottom: spaces.spacingXl,
            columnGap: spaces.spacingMd,
            rowGap: spaces.spacingXxl,
            display: "grid",
            width: "100%"
        },
        sample: {
            width: shapeConfigs.drawerWidth,
            flexDirection: "column",
            maxWidth: "100%",
            display: "flex"
        },
        squircle: {
            height: avatarSizes.xLarge,
            width: avatarSizes.xLarge
        },
        densityRow: {
            justifyContent: "center",
            alignItems: "flex-start",
            gap: spaces.spacingXxl,
            flexWrap: "wrap",
            display: "flex",
            width: "100%"
        },
        demoColumn: {
            flexDirection: "column",
            gap: spaces.spacingLg,
            alignItems: "center",
            display: "flex",
            width: "100%"
        }
    }) as const, [
        shapeConfigs,
        avatarSizes,
        spaces
    ]);
};

const CellText = ({
    color = "high",
    text
}: {
    color?: "high" | "mid" | "low";
    text: string;
}) => <Text
    variant="bodyMediumSize"
    color={color}
>
    {renderTerms(text)}
</Text>;

const SpecTable = ({
    headers,
    rows
}: {
    rows: Array<Array<ReactNode>>;
    headers: Array<string>;
}) => {
    const {
        spaces
    } = NCoreUIKitTheme.useContext();

    return <DocsTable
        rows={rows.map((row) => row.map((cell, index) => <View
            style={{
                paddingVertical: spaces.spacingSm
            }}
            key={index}
        >
            {
                typeof cell === "string" ?
                    <CellText
                        text={cell}
                    />
                :
                    cell
            }
        </View>))}
        headers={headers}
    />;
};

const SpaceCell = ({
    value
}: {
    value: number;
}) => {
    const {
        language
    } = useDocsI18n();

    const {
        colors,
        spaces
    } = NCoreUIKitTheme.useContext();

    return <View
        style={[
            stylesheet.stack,
            {
                gap: spaces.spacingXs
            }
        ]}
    >
        <Text
            variant="monoMediumSize"
            color="high"
        >
            {`${value} px`}
        </Text>
        <View
            style={{
                backgroundColor: colors.content.icon.low,
                height: spaces.spacingSm,
                width: value
            }}
        />
        <Text
            variant="bodySmallSize"
            color="low"
        >
            {getGridText(value, getKitLocale(language))}
        </Text>
    </View>;
};

const RadiusSample = ({
    radius,
    size
}: {
    radius: number;
    size: number;
}) => {
    const {
        shapeConfigs,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext();

    return <View
        style={[
            stylesheet.stack,
            {
                gap: spaces.spacingXs
            }
        ]}
    >
        <View
            style={{
                backgroundColor: colors.content.container.selected,
                borderColor: colors.content.border.default,
                borderWidth: borders.line,
                ...getCornerStyle({
                    radius: radius,
                    shapeConfigs
                }),
                height: size,
                width: size
            }}
        />
        <Text
            variant="monoMediumSize"
            color="high"
        >
            {String(radius)}
        </Text>
    </View>;
};

const HitAreaFigure = ({
    target,
    label
}: {
    target: number;
    label: string;
}) => {
    const {
        touchTargets,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext();

    const frame = touchTargets.preferred + spaces.spacingMd * 2;

    const offset = (frame - target) / 2;

    return <View
        style={[
            stylesheet.figure,
            {
                gap: spaces.spacingXs
            }
        ]}
    >
        <View
            style={[
                stylesheet.figureStage,
                {
                    height: frame,
                    width: frame
                }
            ]}
        >
            <View
                style={StyleSheet.absoluteFill}
            >
                <svg
                    viewBox={`0 0 ${frame} ${frame}`}
                    aria-hidden={true}
                    height={frame}
                    width={frame}
                >
                    <rect
                        strokeDasharray={`${spaces.spacingXs} ${spaces.spacingXs}`}
                        fill={colors.content.container.selected}
                        stroke={colors.content.icon.low}
                        strokeWidth={borders.line}
                        height={target}
                        width={target}
                        x={offset}
                        y={offset}
                    />
                </svg>
            </View>
            <Icon
                icon={XIcon}
                color="high"
                size="small"
            />
        </View>
        <Text
            variant="labelMediumSize"
            color="high"
        >
            {label}
        </Text>
        <Text
            variant="bodySmallSize"
            color="mid"
        >
            {`${target} × ${target} px`}
        </Text>
    </View>;
};

const ConcentricFigure = ({
    inner,
    inset,
    outer,
    label
}: {
    label: string;
    inset: number;
    inner: number;
    outer: number;
}) => {
    const {
        shapeConfigs,
        avatarSizes,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext();

    const width = shapeConfigs.fieldMinWidth;

    const height = avatarSizes.xLarge;

    const half = borders.line / 2;

    return <View
        style={[
            stylesheet.figure,
            {
                gap: spaces.spacingXs
            }
        ]}
    >
        <svg
            viewBox={`0 0 ${width} ${height}`}
            aria-label={label}
            height={height}
            width={width}
            role="img"
        >
            <rect
                fill={colors.content.container.raised}
                stroke={colors.content.border.default}
                height={height - borders.line}
                width={width - borders.line}
                strokeWidth={borders.line}
                rx={outer}
                x={half}
                y={half}
            />
            <rect
                fill={colors.content.container.selected}
                stroke={colors.content.icon.low}
                height={height - inset * 2}
                strokeWidth={borders.line}
                width={width - inset * 2}
                rx={inner}
                x={inset}
                y={inset}
            />
        </svg>
        <Text
            variant="labelMediumSize"
            color="high"
        >
            {label}
        </Text>
        <Text
            variant="bodySmallSize"
            color="mid"
        >
            {`rx ${inner}`}
        </Text>
    </View>;
};

const ConcentricDemo = () => {
    const {
        t
    } = useDocsI18n();

    const {
        radiuses,
        spaces
    } = NCoreUIKitTheme.useContext();

    const styles = useSpacingStyles();

    const [
        insetName,
        setInsetName
    ] = useState<SpaceName>("spacingSm");

    const inset = spaces[insetName];

    const outer = radiuses.xl;

    const inner = concentric({
        role: outer,
        outer,
        inset
    });

    const handleChange = (value: string | Array<string>) => {
        if(typeof value === "string" && isInsetName(value)) {
            setInsetName(value);
        }
    };

    return <Preview
        caption={t("spacingPage.concentricCaption", [
            outer
        ])}
    >
        <div
            style={styles.demoColumn}
        >
            <View
                style={stylesheet.toggle}
            >
                <ToggleGroup
                    title={t("spacingPage.concentricInsetLabel")}
                    onValueChange={handleChange}
                    attachBehaviour="attached"
                    variant="outline"
                    value={insetName}
                >
                    {
                        CONCENTRIC_INSETS.map((name) => <ToggleGroup.Item
                            title={`${name.replace("spacing", "")} · ${spaces[name]}`}
                            value={name}
                            key={name}
                        />)
                    }
                </ToggleGroup>
            </View>
            <View
                style={[
                    stylesheet.centeredRow,
                    {
                        gap: spaces.spacingXl
                    }
                ]}
            >
                <ConcentricFigure
                    label={t("spacingPage.concentricWrong")}
                    outer={outer}
                    inset={inset}
                    inner={outer}
                />
                <ConcentricFigure
                    label={t("spacingPage.concentricRight")}
                    outer={outer}
                    inset={inset}
                    inner={inner}
                />
            </View>
            <Text
                variant="monoMediumSize"
                style={stylesheet.label}
                color="high"
            >
                {t("spacingPage.concentricReadout", [
                    outer,
                    inset,
                    inner
                ])}
            </Text>
        </div>
    </Preview>;
};

const SquircleDemo = () => {
    const {
        language,
        t
    } = useDocsI18n();

    const {
        shapeConfigs,
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext();

    const styles = useSpacingStyles();

    const isSupported = getIsCornerShapeSupported();

    const isSquircle = shapeConfigs.cornerShape === "squircle";

    const radius = radiuses.xl;

    const scaledRadius = radius * shapeConfigs.squircleRadiusScale;

    const shapeStyle = {
        backgroundColor: colors.content.container.selected,
        borderColor: colors.content.border.default,
        borderWidth: borders.line
    };

    const squircleStyle = isSupported && isSquircle ? {
        borderRadius: scaledRadius,
        ...webStyle({
            cornerShape: "squircle"
        })
    } : {
        borderRadius: radius
    };

    const getReadout = () => {
        if(!isSquircle) {
            return t("spacingPage.squircleOff", [
                shapeConfigs.cornerShape
            ]);
        }

        if(!isSupported) {
            return t("spacingPage.squircleUnsupported");
        }

        return t("spacingPage.squircleSupported", [
            radius,
            formatNumber(shapeConfigs.squircleRadiusScale, getKitLocale(language)),
            formatNumber(scaledRadius, getKitLocale(language))
        ]);
    };

    const renderShape = (label: string, style: object) => <View
        style={[
            stylesheet.figure,
            {
                gap: spaces.spacingSm
            }
        ]}
    >
        <View
            style={[
                styles.squircle,
                shapeStyle,
                style
            ]}
        />
        <Text
            variant="labelMediumSize"
            color="high"
        >
            {label}
        </Text>
    </View>;

    return <Preview
        caption={t("spacingPage.squircleCaption", [
            radius
        ])}
    >
        <div
            style={styles.demoColumn}
        >
            <View
                style={[
                    stylesheet.centeredRow,
                    {
                        gap: spaces.spacingXxl
                    }
                ]}
            >
                {
                    renderShape(t("spacingPage.squircleRound"), {
                        borderRadius: radius
                    })
                }
                {renderShape(t("spacingPage.squircleKit"), squircleStyle)}
            </View>
            <Text
                variant="bodyMediumSize"
                style={stylesheet.label}
                color="mid"
            >
                {getReadout()}
            </Text>
        </div>
    </Preview>;
};

const DensitySample = ({
    density
}: {
    density: GapPropagationType;
}) => {
    const {
        t
    } = useDocsI18n();

    const {
        controlHeights,
        spaces
    } = NCoreUIKitTheme.useContext({
        gapPropagation: density
    });

    const styles = useSpacingStyles();

    const customTheme = {
        gapPropagation: density
    };

    return <div
        style={{
            ...styles.sample,
            gap: spaces.spacingMd
        }}
    >
        <View
            style={{
                gap: spaces.spacingXxs
            }}
        >
            <Text
                variant="titleSmallSize"
                color="high"
            >
                {density}
            </Text>
            <Text
                variant="bodySmallSize"
                color="mid"
            >
                {t("spacingPage.densitySampleNote", [
                    controlHeights.medium,
                    spaces.spacingMd
                ])}
            </Text>
        </View>
        <TextInput
            placeholder={t("spacingPage.demoFieldPlaceholder")}
            title={t("spacingPage.demoFieldTitle")}
            customTheme={customTheme}
            spreadBehaviour="stretch"
        />
        <View
            style={[
                stylesheet.row,
                {
                    gap: spaces.spacingSm
                }
            ]}
        >
            <Button
                title={t("spacingPage.demoSave")}
                onPress={handleDemoPress}
                customTheme={customTheme}
                variant="filled"
            />
            <Button
                title={t("spacingPage.demoCancel")}
                onPress={handleDemoPress}
                customTheme={customTheme}
                variant="outline"
            />
        </View>
        <View
            style={[
                stylesheet.row,
                {
                    gap: spaces.spacingSm
                }
            ]}
        >
            <Chip
                title={t("spacingPage.demoChipDraft")}
                customTheme={customTheme}
            />
            <Chip
                title={t("spacingPage.demoChipShared")}
                customTheme={customTheme}
            />
        </View>
    </div>;
};

const DensityDemo = () => {
    const {
        t
    } = useDocsI18n();

    const styles = useSpacingStyles();

    return <Preview
        caption={t("spacingPage.densityCaption")}
    >
        <div
            style={styles.densityRow}
        >
            <DensitySample
                density="spacious"
            />
            <DensitySample
                density="compact"
            />
        </div>
    </Preview>;
};

const SharpnessDemo = () => {
    const {
        t
    } = useDocsI18n();

    const {
        spaces
    } = NCoreUIKitTheme.useContext();

    const styles = useSpacingStyles();

    const [
        sharpness,
        setSharpness
    ] = useState<SharpnessType>("soft");

    const customTheme = {
        sharpness
    };

    const handleChange = (value: string | Array<string>) => {
        if(typeof value === "string" && isSharpness(value)) {
            setSharpness(value);
        }
    };

    return <Preview
        caption={t("spacingPage.radiiCaption")}
    >
        <div
            style={styles.demoColumn}
        >
            <View
                style={stylesheet.toggle}
            >
                <ToggleGroup
                    title={t("spacingPage.radiiToggleLabel")}
                    onValueChange={handleChange}
                    attachBehaviour="attached"
                    variant="outline"
                    value={sharpness}
                >
                    {
                        SHARPNESS_SETS.map((name) => <ToggleGroup.Item
                            title={name}
                            value={name}
                            key={name}
                        />)
                    }
                </ToggleGroup>
            </View>
            <div
                style={styles.sample}
            >
                <Card
                    customTheme={customTheme}
                    spreadBehaviour="stretch"
                    variant="outline"
                >
                    <Card.Header>
                        <Card.Title>
                            {t("spacingPage.demoCardTitle")}
                        </Card.Title>
                        <Card.Description>
                            {t("spacingPage.demoCardText")}
                        </Card.Description>
                    </Card.Header>
                    <Card.Content>
                        <View
                            style={{
                                gap: spaces.spacingMd
                            }}
                        >
                            <TextInput
                                placeholder={t("spacingPage.demoFieldPlaceholder")}
                                title={t("spacingPage.demoFieldTitle")}
                                customTheme={customTheme}
                                spreadBehaviour="stretch"
                            />
                            <View
                                style={[
                                    stylesheet.row,
                                    {
                                        gap: spaces.spacingSm
                                    }
                                ]}
                            >
                                <Chip
                                    title={t("spacingPage.demoChipDraft")}
                                    customTheme={customTheme}
                                />
                                <Chip
                                    title={t("spacingPage.demoChipShared")}
                                    customTheme={customTheme}
                                />
                            </View>
                        </View>
                    </Card.Content>
                    <Card.Footer
                        align="end"
                    >
                        <Button
                            title={t("spacingPage.demoCancel")}
                            onPress={handleDemoPress}
                            customTheme={customTheme}
                            variant="outline"
                        />
                        <Button
                            title={t("spacingPage.demoSave")}
                            onPress={handleDemoPress}
                            customTheme={customTheme}
                            variant="filled"
                        />
                    </Card.Footer>
                </Card>
            </div>
        </div>
    </Preview>;
};

const ElevationDemo = () => {
    const {
        t
    } = useDocsI18n();

    const {
        shadowAlpha,
        elevations,
        colors
    } = NCoreUIKitTheme.useContext();

    const styles = useSpacingStyles();

    const levels = Object.keys(elevations) as Array<ElevationLevelType>;

    return <Preview
        caption={t("spacingPage.elevationCaption", [
            levels.length
        ])}
    >
        <div
            style={styles.elevationGrid}
        >
            {
                levels.map((level) => {
                    const users = getSurfaceUsers(level);

                    return <Card
                        style={getElevationStyle({
                            shadowColor: colors.system.shadow,
                            shadowAlpha,
                            elevations,
                            level
                        })}
                        spreadBehaviour="stretch"
                        variant="filled"
                        size="small"
                        key={level}
                    >
                        <Card.Header>
                            <Card.Title>
                                {level}
                            </Card.Title>
                            <Card.Description>
                                {users.length ? formatList(users) : t("spacingPage.elevationFlat")}
                            </Card.Description>
                        </Card.Header>
                    </Card>;
                })
            }
        </div>
    </Preview>;
};

export const getSpacingToc = (t: DocsTranslate): Array<DocsTocItem> => [
    {
        title: t("section.overview"),
        id: "overview"
    },
    {
        title: t("spacingPage.spacingTitle"),
        id: "spacing"
    },
    {
        title: t("spacingPage.densityTitle"),
        id: "density"
    },
    {
        title: t("spacingPage.controlsTitle"),
        id: "controls"
    },
    {
        title: t("spacingPage.touchTitle"),
        id: "touch"
    },
    {
        title: t("spacingPage.iconsTitle"),
        id: "icons"
    },
    {
        title: t("spacingPage.radiiTitle"),
        id: "radii"
    },
    {
        title: t("spacingPage.concentricTitle"),
        id: "concentric"
    },
    {
        title: t("spacingPage.squircleTitle"),
        id: "squircle"
    },
    {
        title: t("spacingPage.bordersTitle"),
        id: "borders"
    },
    {
        title: t("spacingPage.elevationTitle"),
        id: "elevation"
    },
    {
        title: t("spacingPage.layersTitle"),
        id: "layers"
    },
    {
        title: t("spacingPage.widthsTitle"),
        id: "widths"
    },
    {
        title: t("spacingPage.breakpointsTitle"),
        id: "breakpoints"
    }
];

const SpacingPage = () => {
    const {
        language,
        t
    } = useDocsI18n();

    const theme = NCoreUIKitTheme.useContext();

    const tokenSets = getTokenSets(NCoreUIKitTheme.projectThemes);

    const spacious = getDensitySet(tokenSets, "spacious");

    const compact = getDensitySet(tokenSets, "compact");

    const soft = NCoreUIKitTheme.useContext({
        sharpness: "soft"
    });

    const sharp = NCoreUIKitTheme.useContext({
        sharpness: "sharp"
    });

    const rounded = NCoreUIKitTheme.useContext({
        sharpness: "rounded"
    });

    const light = NCoreUIKitTheme.useContext({
        themeKey: "light"
    });

    const dark = NCoreUIKitTheme.useContext({
        themeKey: "dark"
    });

    const pointerType = usePointerType();

    const breakpoint = useBreakpoint();

    const {
        width: windowWidth
    } = useWindowDimensions();

    const locale = getKitLocale(language);

    const {
        controlPaddings,
        controlHeights,
        inlineSpaces,
        shapeConfigs,
        touchTargets,
        avatarSizes,
        shadowAlpha,
        elevations,
        typography,
        iconSizes,
        radiuses,
        borders
    } = theme;

    const spaceNames = Object.keys(spacious.spaces) as Array<SpaceName>;

    const spaciousSteps = [
        ...new Set(getTokenValues(spacious.spaces))
    ].sort((first, second) => first - second);

    const smallestRatio = Math.min(...spaciousSteps.slice(1).map((step, index) => step / (spaciousSteps[index] ?? step)));

    const changedSpaces = getChangedKeys(spacious.spaces, compact.spaces);

    const keptSpaces = getEqualKeys(spacious.spaces, compact.spaces);

    const typeStyleNames = Object.keys(spacious.typography) as Array<keyof DocsStyleTokens["typography"]>;

    const changedTypeStyles = typeStyleNames.filter((name) => JSON.stringify(spacious.typography[name]) !== JSON.stringify(compact.typography[name]));

    const isCoarse = pointerType === "coarse";

    const sharpnessContexts = [
        soft,
        sharp,
        rounded
    ];

    const finiteRadii = sharpnessContexts.flatMap((context) => getTokenValues(context.radiuses)).filter((value) => value < radiuses.full);

    const largestFiniteRadius = Math.max(...finiteRadii);

    const radiusSampleSize = getTokenValues(avatarSizes).sort((first, second) => first - second).find((size) => size > largestFiniteRadius * 2) ?? largestFiniteRadius * 2 + spacious.spaces.spacingMd;

    const radiusScale = [
        radiuses.xs,
        radiuses.sm,
        radiuses.md,
        radiuses.lg,
        radiuses.xl
    ];

    const stepDownMeasures = getControlMeasures({
        controlPaddings,
        controlHeights,
        size: "medium",
        shapeConfigs,
        iconSizes,
        step: -1
    });

    const hitArea = getHitArea({
        visualSize: iconSizes.small,
        touchTargets,
        pointerType
    });

    const iconRows = CONTROL_SIZE_NAMES.map((size) => {
        const labelStyle = typography[CONTROL_SIZES[size].labelVariant];

        return {
            icon: iconSizes[CONTROL_SIZES[size].iconSize],
            label: CONTROL_SIZES[size].labelVariant,
            raw: labelStyle.fontSize * ICON_SCALE,
            lineHeight: labelStyle.lineHeight,
            fontSize: labelStyle.fontSize,
            size
        };
    });

    const levels = Object.keys(elevations) as Array<ElevationLevelType>;

    const keyRatios = [
        ...new Set(levels.flatMap((level) => elevations[level].filter((item) => item.offset > 0).map((item) => item.blur / item.offset)))
    ];

    const formatLayer = (layer?: DocsStyleTokens["elevations"][ElevationLevelType][number]) => layer ? t("spacingPage.layerCell", [
        layer.offset,
        layer.blur,
        formatPercent(shadowAlpha * layer.alpha, locale)
    ]) : "—";

    const formatChartValue = (name: ChartName) => {
        const value = shapeConfigs[name];

        if(typeof value === "number") {
            return name === "chartDonutRatio" ? formatNumber(value, locale) : `${value} px`;
        }

        return `${formatSteps(getSizeValues(value))} px`;
    };

    const breakpointRows = [
        [
            "compact",
            `< ${shapeConfigs.compactBreakpoint} px`,
            t("spacingPage.breakpointCompactExample")
        ],
        [
            "medium",
            `${shapeConfigs.compactBreakpoint} – ${shapeConfigs.wideBreakpoint - 1} px`,
            t("spacingPage.breakpointMediumExample")
        ],
        [
            "wide",
            `≥ ${shapeConfigs.wideBreakpoint} px`,
            t("spacingPage.breakpointWideExample")
        ]
    ];

    return <Article>
        <PageHeader
            breadcrumbs={[
                {
                    label: t("breadcrumb.docs"),
                    href: "/"
                },
                {
                    label: t("nav.sectionFoundations")
                }
            ]}
            lead={t("foundation.spacingLead")}
            title={t("page.spacing")}
        />
        <Section
            title={t("section.overview")}
            id="overview"
        >
            <Prose
                text={t("spacingPage.overviewText")}
            />
            <Prose
                text={t("spacingPage.overviewLiveText")}
            />
            <Stats
                items={[
                    {
                        label: t("spacingPage.statDensity"),
                        value: theme.resolvedGapPropagation
                    },
                    {
                        label: t("spacingPage.statSharpness"),
                        value: theme.activeSharpness
                    },
                    {
                        label: t("spacingPage.statPointer"),
                        value: pointerType
                    },
                    {
                        label: t("spacingPage.statBreakpoint"),
                        value: breakpoint
                    }
                ]}
            />
            <Prose
                text={t("spacingPage.overviewMotionText")}
                isMuted={true}
            />
        </Section>
        <Section
            title={t("spacingPage.spacingTitle")}
            id="spacing"
        >
            <Prose
                text={t("spacingPage.spacingText", [
                    spaciousSteps.length,
                    formatList(spaciousSteps)
                ])}
            />
            <Prose
                text={t("spacingPage.spacingStepText", [
                    formatNumber(smallestRatio, locale)
                ])}
            />
            <SpecTable
                rows={spaceNames.map((name) => [
                    <InlineCode
                        key="name"
                    >
                        {name}
                    </InlineCode>,
                    <SpaceCell
                        value={spacious.spaces[name]}
                        key="spacious"
                    />,
                    <SpaceCell
                        value={compact.spaces[name]}
                        key="compact"
                    />
                ])}
                headers={[
                    t("spacingPage.headerToken"),
                    "spacious",
                    "compact"
                ]}
            />
            <Prose
                text={t("spacingPage.spacingUseText")}
            />
            <BulletList
                items={[
                    renderTerms(t("spacingPage.spacingUseTight")),
                    renderTerms(t("spacingPage.spacingUseInner")),
                    renderTerms(t("spacingPage.spacingUseSection"))
                ]}
            />
            <Prose
                text={t("spacingPage.spacingOtherText", [
                    inlineSpaces.required
                ])}
            />
            <Prose
                text={t("spacingPage.spacingCodeText")}
            />
            <CodeBlock
                code={SPACING_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("spacingPage.densityTitle")}
            id="density"
        >
            <Prose
                text={t("spacingPage.densityText")}
            />
            <BulletList
                items={[
                    renderTerms(t("spacingPage.densitySpacesItem", [
                        changedSpaces.map((name) => `${name} ${spacious.spaces[name]} → ${compact.spaces[name]}`).join(", ")
                    ])),
                    renderTerms(t("spacingPage.densityControlsItem", [
                        formatSteps(getSizeValues(spacious.controlHeights)),
                        formatSteps(getSizeValues(compact.controlHeights)),
                        formatSteps(getSizeValues(spacious.controlPaddings)),
                        formatSteps(getSizeValues(compact.controlPaddings))
                    ])),
                    renderTerms(changedTypeStyles.length ? t("spacingPage.densityKeptDiffItem", [
                        formatList(keptSpaces),
                        changedTypeStyles.length,
                        typeStyleNames.length
                    ]) : t("spacingPage.densityKeptItem", [
                        formatList(keptSpaces),
                        typeStyleNames.length
                    ]))
                ]}
            />
            <DensityDemo/>
            <Prose
                text={t("spacingPage.densityWhyText", [
                    spacious.controlHeights.medium,
                    compact.controlHeights.medium,
                    formatPercent(spacious.controlHeights.medium / compact.controlHeights.medium - 1, locale)
                ])}
            />
            <Prose
                text={t("spacingPage.densityTouchText", [
                    compact.controlHeights.small
                ])}
            />
            <Prose
                text={t("spacingPage.densityNowText", [
                    pointerType,
                    theme.activeGapPropagation,
                    theme.resolvedGapPropagation
                ])}
                isMuted={true}
            />
            <Prose
                text={t("spacingPage.densityHowText")}
            />
            <CodeBlock
                code={DENSITY_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("spacingPage.controlsTitle")}
            id="controls"
        >
            <Prose
                text={t("spacingPage.controlsText")}
            />
            <SpecTable
                rows={CONTROL_SIZE_NAMES.map((size) => [
                    <InlineCode
                        key="size"
                    >
                        {size}
                    </InlineCode>,
                    `${t("spacingPage.cellHeight", [
                        spacious.controlHeights[size]
                    ])}\n${t("spacingPage.cellPadding", [
                        spacious.controlPaddings[size]
                    ])}`,
                    `${t("spacingPage.cellHeight", [
                        compact.controlHeights[size]
                    ])}\n${t("spacingPage.cellPadding", [
                        compact.controlPaddings[size]
                    ])}`,
                    `${t("spacingPage.cellIcon", [
                        iconSizes[CONTROL_SIZES[size].iconSize]
                    ])}\n${CONTROL_SIZES[size].labelVariant}`
                ])}
                headers={[
                    t("spacingPage.headerSize"),
                    "spacious",
                    "compact",
                    t("spacingPage.headerIconLabel")
                ]}
            />
            {
                isCoarse ?
                    <Callout>
                        {t("spacingPage.compactCoarseNote")}
                    </Callout>
                :
                    null
            }
            <Prose
                text={t("spacingPage.controlsWhyText", [
                    spacious.controlHeights.large,
                    touchTargets.preferred,
                    compact.controlHeights.small,
                    touchTargets.fine
                ])}
            />
            <Prose
                text={t("spacingPage.chipText", [
                    shapeConfigs.chipHeightOffset,
                    shapeConfigs.chipPaddingOffset,
                    stepDownMeasures.minHeight,
                    stepDownMeasures.paddingHorizontal
                ])}
            />
            <Prose
                text={t("spacingPage.controlsCodeText")}
            />
            <CodeBlock
                code={CONTROL_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("spacingPage.touchTitle")}
            id="touch"
        >
            <Prose
                text={t("spacingPage.touchText")}
            />
            <SpecTable
                headers={[
                    t("spacingPage.headerToken"),
                    t("spacingPage.headerValue"),
                    t("spacingPage.headerStandard"),
                    t("spacingPage.headerWhere")
                ]}
                rows={TOUCH_ROWS.map((row) => [
                    <InlineCode
                        key="name"
                    >
                        {row.name}
                    </InlineCode>,
                    `${touchTargets[row.name]} px`,
                    t(row.standardKey),
                    t(row.whereKey)
                ])}
            />
            <Prose
                text={t("spacingPage.touchHandleText", [
                    touchTargets.handle,
                    touchTargets.fine
                ])}
            />
            <SubTitle>
                {t("spacingPage.hitTitle")}
            </SubTitle>
            <Prose
                text={t("spacingPage.touchHowText")}
            />
            <Preview
                caption={t("spacingPage.touchCaption", [
                    iconSizes.small
                ])}
            >
                <HitAreaFigure
                    label={`fine · ${touchTargets.fine}`}
                    target={touchTargets.fine}
                />
                <HitAreaFigure
                    label={`coarse · ${touchTargets.coarse}`}
                    target={touchTargets.coarse}
                />
                <HitAreaFigure
                    label={`preferred · ${touchTargets.preferred}`}
                    target={touchTargets.preferred}
                />
            </Preview>
            <Prose
                text={t("spacingPage.touchNowText", [
                    pointerType,
                    iconSizes.small,
                    hitArea.minWidth
                ])}
                isMuted={true}
            />
            <Prose
                text={t("spacingPage.touchMarginText")}
            />
            <BulletList
                items={[
                    renderTerms(t("spacingPage.touchRuleQuery")),
                    renderTerms(t("spacingPage.touchRuleDensity", [
                        compact.controlHeights.small,
                        touchTargets.fine
                    ]))
                ]}
            />
        </Section>
        <Section
            title={t("spacingPage.iconsTitle")}
            id="icons"
        >
            <Prose
                text={t("spacingPage.iconsText")}
            />
            <SpecTable
                rows={iconRows.map((row) => [
                    <InlineCode
                        key="size"
                    >
                        {row.size}
                    </InlineCode>,
                    `${row.label}\n${row.fontSize} px`,
                    `${formatNumber(row.raw, locale)} px`,
                    `${row.icon} px`,
                    `${row.lineHeight} px`
                ])}
                headers={[
                    t("spacingPage.headerSize"),
                    t("spacingPage.headerLabel"),
                    t("spacingPage.headerRaw"),
                    t("spacingPage.headerIcon"),
                    t("spacingPage.headerLineHeight")
                ]}
            />
            <Prose
                text={t("spacingPage.iconsWhyText", [
                    formatNumber(iconRows[0]?.raw ?? 0, locale),
                    formatNumber(iconRows[1]?.raw ?? 0, locale),
                    iconSizes.medium
                ])}
            />
            <Prose
                text={t("spacingPage.iconsRowText")}
            />
            <SpecTable
                rows={[
                    [
                        `≤ small (${spacious.controlHeights.small} / ${compact.controlHeights.small} px)`,
                        <InlineCode
                            key="gap"
                        >
                            spacingSm
                        </InlineCode>,
                        `${spacious.spaces.spacingSm} px`,
                        `${compact.spaces.spacingSm} px`
                    ],
                    [
                        `≤ medium (${spacious.controlHeights.medium} / ${compact.controlHeights.medium} px)`,
                        <InlineCode
                            key="gap"
                        >
                            spacingSm + spacingXs
                        </InlineCode>,
                        `${spacious.spaces.spacingSm + spacious.spaces.spacingXs} px`,
                        `${compact.spaces.spacingSm + compact.spaces.spacingXs} px`
                    ],
                    [
                        "> medium",
                        <InlineCode
                            key="gap"
                        >
                            spacingMd
                        </InlineCode>,
                        `${spacious.spaces.spacingMd} px`,
                        `${compact.spaces.spacingMd} px`
                    ]
                ]}
                headers={[
                    t("spacingPage.headerRow"),
                    t("spacingPage.headerGap"),
                    "spacious",
                    "compact"
                ]}
            />
        </Section>
        <Section
            title={t("spacingPage.radiiTitle")}
            id="radii"
        >
            <Prose
                text={t("spacingPage.radiiText", [
                    formatList(radiusScale)
                ])}
            />
            <Prose
                text={t("spacingPage.radiiSetsText")}
            />
            <SpecTable
                rows={RADIUS_ROWS.map((name) => [
                    <InlineCode
                        key="name"
                    >
                        {name}
                    </InlineCode>,
                    ...sharpnessContexts.map((context, index) => <RadiusSample
                        radius={context.radiuses[name]}
                        key={SHARPNESS_SETS[index]}
                        size={radiusSampleSize}
                    />)
                ])}
                headers={[
                    t("spacingPage.headerToken"),
                    ...SHARPNESS_SETS
                ]}
            />
            <SharpnessDemo/>
            <Prose
                text={t("spacingPage.radiiNestText", [
                    soft.radiuses.surface - soft.radiuses.actions
                ])}
            />
            <Prose
                text={t("spacingPage.radiiFullText", [
                    radiuses.full
                ])}
            />
        </Section>
        <Section
            title={t("spacingPage.concentricTitle")}
            id="concentric"
        >
            <Prose
                text={t("spacingPage.concentricText")}
            />
            <ConcentricDemo/>
            <Prose
                text={t("spacingPage.concentricHelperText")}
            />
            <BulletList
                items={[
                    renderTerms(t("spacingPage.concentricRuleRole")),
                    renderTerms(t("spacingPage.concentricRuleFar")),
                    renderTerms(t("spacingPage.concentricRuleInset")),
                    renderTerms(t("spacingPage.concentricRuleCapsule"))
                ]}
            />
            <CodeBlock
                code={CONCENTRIC_CODE}
                language="tsx"
            />
            <Prose
                text={t("spacingPage.concentricUseText")}
            />
        </Section>
        <Section
            title={t("spacingPage.squircleTitle")}
            id="squircle"
        >
            <Prose
                text={t("spacingPage.squircleText", [
                    shapeConfigs.cornerShape
                ])}
            />
            <SquircleDemo/>
            <Prose
                text={t("spacingPage.squircleHowText")}
            />
            <BulletList
                items={[
                    renderTerms(t("spacingPage.squircleWeb", [
                        formatNumber(shapeConfigs.squircleRadiusScale, locale)
                    ])),
                    renderTerms(t("spacingPage.squircleIos")),
                    renderTerms(t("spacingPage.squircleOther")),
                    renderTerms(t("spacingPage.squircleExempt"))
                ]}
            />
            <Prose
                text={t("spacingPage.squircleScaleText", [
                    formatNumber(shapeConfigs.squircleRadiusScale, locale)
                ])}
            />
        </Section>
        <Section
            title={t("spacingPage.bordersTitle")}
            id="borders"
        >
            <Prose
                text={t("spacingPage.bordersText", [
                    borders.line
                ])}
            />
            <SpecTable
                headers={[
                    t("spacingPage.headerToken"),
                    t("spacingPage.headerValue"),
                    t("spacingPage.headerUse")
                ]}
                rows={BORDER_ROWS.map((row) => [
                    <InlineCode
                        key="name"
                    >
                        {row.name}
                    </InlineCode>,
                    `${borders[row.name]} px`,
                    t(row.textKey)
                ])}
            />
            <Prose
                text={t("spacingPage.separatorText")}
            />
            <Prose
                text={t("spacingPage.marksText")}
            />
            <Prose
                text={t("spacingPage.focusText", [
                    borders.focus,
                    borders.halo
                ])}
            />
        </Section>
        <Section
            title={t("spacingPage.elevationTitle")}
            id="elevation"
        >
            <Prose
                text={t("spacingPage.elevationText", [
                    levels.length
                ])}
            />
            <SpecTable
                rows={levels.map((level) => {
                    const layers = elevations[level];

                    const keyLayer = layers.find((item) => item.offset > 0);

                    const users = getSurfaceUsers(level);

                    return [
                        <InlineCode
                            key="level"
                        >
                            {level}
                        </InlineCode>,
                        users.length ? formatList(users) : t("spacingPage.elevationFlat"),
                        formatLayer(layers.find((item) => item.offset === 0)),
                        formatLayer(keyLayer),
                        keyLayer ? formatNumber(keyLayer.blur / keyLayer.offset, locale) : "—"
                    ];
                })}
                headers={[
                    t("spacingPage.headerLevel"),
                    t("spacingPage.headerExample"),
                    t("spacingPage.headerAmbient"),
                    t("spacingPage.headerKey"),
                    t("spacingPage.headerRatio")
                ]}
            />
            <Prose
                text={t("spacingPage.elevationColorText", [
                    formatNumber(light.shadowAlpha, locale),
                    formatNumber(dark.shadowAlpha, locale),
                    theme.activeTheme
                ])}
            />
            <ElevationDemo/>
            <Prose
                text={t("spacingPage.elevationRatioText", [
                    formatList(keyRatios.map((ratio) => formatNumber(ratio, locale)))
                ])}
            />
            <Prose
                text={t("spacingPage.elevationEdgeText")}
            />
            <Prose
                text={t("spacingPage.elevationCodeText")}
            />
            <CodeBlock
                code={ELEVATION_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("spacingPage.layersTitle")}
            id="layers"
        >
            <Prose
                text={t("spacingPage.layersText")}
            />
            <SpecTable
                rows={LAYER_ROWS.map((row) => [
                    <InlineCode
                        key="name"
                    >
                        {row.name}
                    </InlineCode>,
                    String(shapeConfigs.layers[row.name]),
                    t(row.textKey),
                    row.users
                ])}
                headers={[
                    t("spacingPage.headerLayer"),
                    t("spacingPage.headerValue"),
                    t("spacingPage.headerPurpose"),
                    t("spacingPage.headerUsers")
                ]}
            />
            <Prose
                text={t("spacingPage.layersOffsetText")}
            />
        </Section>
        <Section
            title={t("spacingPage.widthsTitle")}
            id="widths"
        >
            <Prose
                text={t("spacingPage.widthsText")}
            />
            <SpecTable
                rows={WIDTH_ROWS.map((row) => [
                    <InlineCode
                        key="name"
                    >
                        {row.name}
                    </InlineCode>,
                    `${shapeConfigs[row.name]} px`,
                    row.users
                ])}
                headers={[
                    t("spacingPage.headerToken"),
                    t("spacingPage.headerValue"),
                    t("spacingPage.headerUsers")
                ]}
            />
            <Prose
                text={t("spacingPage.widthsReadingText", [
                    shapeConfigs.readingMaxWidth,
                    typography.readingMediumSize.fontSize
                ])}
            />
            <Prose
                text={t("spacingPage.widthsDialogText", [
                    shapeConfigs.dialogMaxWidth,
                    formatPercent(shapeConfigs.sheetWidthRatio, locale)
                ])}
            />
            <Prose
                text={t("spacingPage.widthsShellText", [
                    shapeConfigs.drawerWidth,
                    shapeConfigs.contentMaxWidth,
                    shapeConfigs.sideListWidth,
                    spacious.spaces.spacingXxl,
                    shapeConfigs.drawerWidth + shapeConfigs.contentMaxWidth + shapeConfigs.sideListWidth + spacious.spaces.spacingXxl * 2
                ])}
            />
            <SubTitle>
                {t("spacingPage.chartTitle")}
            </SubTitle>
            <Prose
                text={t("spacingPage.chartText")}
            />
            <SpecTable
                headers={[
                    t("spacingPage.headerToken"),
                    t("spacingPage.headerValue"),
                    t("spacingPage.headerUse")
                ]}
                rows={CHART_ROWS.map((row) => [
                    <InlineCode
                        key="name"
                    >
                        {row.name}
                    </InlineCode>,
                    formatChartValue(row.name),
                    t(row.textKey)
                ])}
            />
        </Section>
        <Section
            title={t("spacingPage.breakpointsTitle")}
            id="breakpoints"
        >
            <Prose
                text={t("spacingPage.breakpointsText", [
                    Math.round(windowWidth),
                    breakpoint
                ])}
            />
            <SpecTable
                headers={[
                    t("spacingPage.headerClass"),
                    t("spacingPage.headerRange"),
                    t("spacingPage.headerDevice")
                ]}
                rows={breakpointRows.map(([
                    name,
                    range,
                    example
                ]) => [
                    <InlineCode
                        key="name"
                    >
                        {name}
                    </InlineCode>,
                    range ?? "",
                    example ?? ""
                ])}
            />
            <Prose
                text={t("spacingPage.breakpointsWhyText", [
                    shapeConfigs.compactBreakpoint,
                    shapeConfigs.wideBreakpoint,
                    shapeConfigs.drawerWidth,
                    shapeConfigs.wideBreakpoint - shapeConfigs.drawerWidth,
                    shapeConfigs.readingMaxWidth
                ])}
            />
            <Prose
                text={t("spacingPage.breakpointsUsageText", [
                    shapeConfigs.compactBreakpoint
                ])}
            />
        </Section>
    </Article>;
};
export default SpacingPage;
